// Forecast locks — the ONLY place that talks to forecast_locks / _cells / _flows.
//
// A lock is a frozen snapshot of the computed forecast for one operational week
// (Sat→Fri). Lock/unlock is admin-only (enforced by RLS); all members can read.

import { supabase } from "../../lib/supabase";
import type { LockCell, LockFlow } from "../../lib/forecast/lockSnapshot";

export interface ForecastLock {
  id: string;
  weekStart: string; // YYYY-MM-DD (the Saturday)
  label?: string;
  note?: string;
  status: "locked" | "superseded";
  lockedAt: string; // ISO timestamp
  lockedBy?: string | null;
}

interface LockRow {
  id: string;
  week_start: string;
  label: string | null;
  note: string | null;
  status: "locked" | "superseded";
  locked_at: string;
  locked_by: string | null;
}

function rowToLock(r: LockRow): ForecastLock {
  return {
    id: r.id,
    weekStart: r.week_start,
    label: r.label ?? undefined,
    note: r.note ?? undefined,
    status: r.status,
    lockedAt: r.locked_at,
    lockedBy: r.locked_by,
  };
}

/** All currently-active (status='locked') locks for the account, newest week first. */
export async function listActiveLocks(accountId: string): Promise<ForecastLock[]> {
  const { data, error } = await supabase
    .from("forecast_locks")
    .select("*")
    .eq("account_id", accountId)
    .eq("status", "locked")
    .order("week_start", { ascending: false });
  if (error) throw error;
  return (data as LockRow[]).map(rowToLock);
}

/** The active lock for one week, or null if that week is not locked. */
export async function getActiveLockForWeek(accountId: string, weekStart: string): Promise<ForecastLock | null> {
  const { data, error } = await supabase
    .from("forecast_locks")
    .select("*")
    .eq("account_id", accountId)
    .eq("week_start", weekStart)
    .eq("status", "locked")
    .maybeSingle();
  if (error) throw error;
  return data ? rowToLock(data as LockRow) : null;
}

/** The frozen forecast atoms for a lock. */
export async function getLockCells(lockId: string): Promise<LockCell[]> {
  const { data, error } = await supabase
    .from("forecast_lock_cells")
    .select("customer_id,hub_id,date,value")
    .eq("lock_id", lockId);
  if (error) throw error;
  return (data as { customer_id: string; hub_id: string; date: string; value: number }[]).map((r) => ({
    customerId: r.customer_id,
    hubId: r.hub_id,
    date: r.date,
    value: Number(r.value),
  }));
}

/** The frozen origin→branch flow volumes for a lock. */
export async function getLockFlows(lockId: string): Promise<LockFlow[]> {
  const { data, error } = await supabase
    .from("forecast_lock_flows")
    .select("origin_hub_id,branch_id,volume")
    .eq("lock_id", lockId);
  if (error) throw error;
  return (data as { origin_hub_id: string; branch_id: string; volume: number }[]).map((r) => ({
    originHubId: r.origin_hub_id,
    branchId: r.branch_id,
    volume: Number(r.volume),
  }));
}

const CHUNK = 1000;

/**
 * Lock a week: insert the new lock + its snapshot, then supersede any prior active
 * lock for the same week (so exactly one stays active). Returns the new lock.
 * Admin-only via RLS — a non-admin call rejects at the insert.
 */
export async function createLock(
  accountId: string,
  weekStart: string,
  cells: LockCell[],
  flows: LockFlow[],
  opts: { label?: string; note?: string; lockedBy?: string | null } = {}
): Promise<ForecastLock> {
  const { data: header, error: headErr } = await supabase
    .from("forecast_locks")
    .insert({
      account_id: accountId,
      week_start: weekStart,
      label: opts.label?.trim() || null,
      note: opts.note?.trim() || null,
      status: "locked",
      locked_by: opts.lockedBy ?? null,
    })
    .select("*")
    .single();
  if (headErr) throw headErr;
  const lock = rowToLock(header as LockRow);

  // Snapshot cells (chunked).
  for (let i = 0; i < cells.length; i += CHUNK) {
    const rows = cells.slice(i, i + CHUNK).map((c) => ({
      lock_id: lock.id,
      account_id: accountId,
      customer_id: c.customerId,
      hub_id: c.hubId,
      date: c.date,
      value: c.value,
    }));
    const { error } = await supabase.from("forecast_lock_cells").insert(rows);
    if (error) throw error;
  }

  // Snapshot flow shares (chunked).
  for (let i = 0; i < flows.length; i += CHUNK) {
    const rows = flows.slice(i, i + CHUNK).map((f) => ({
      lock_id: lock.id,
      account_id: accountId,
      origin_hub_id: f.originHubId,
      branch_id: f.branchId,
      volume: f.volume,
    }));
    const { error } = await supabase.from("forecast_lock_flows").insert(rows);
    if (error) throw error;
  }

  // Supersede any other active lock for this week (keep the new one active).
  const { error: supErr } = await supabase
    .from("forecast_locks")
    .update({ status: "superseded" })
    .eq("account_id", accountId)
    .eq("week_start", weekStart)
    .eq("status", "locked")
    .neq("id", lock.id);
  if (supErr) throw supErr;

  return lock;
}

/** Unlock a week: mark its active lock superseded (kept for history). */
export async function unlockWeek(accountId: string, weekStart: string): Promise<void> {
  const { error } = await supabase
    .from("forecast_locks")
    .update({ status: "superseded" })
    .eq("account_id", accountId)
    .eq("week_start", weekStart)
    .eq("status", "locked");
  if (error) throw error;
}
